import { useCallback, useEffect, useMemo, useState } from "react";
import { roleService } from "../services/RoleService";
import { Environment, UserAccessSummary, UserRole } from "../types/role";

interface UseRoleAccessOptions {
  uid?: string;
  environment?: Environment;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseRoleAccessReturn {
  // User role and access
  userRole: UserRole | null;
  accessSummary: UserAccessSummary | null;
  accessibleAgents: string[];
  accessiblePlugins: string[];

  // Feature access flags
  canAccessDebugMenu: boolean;
  canAccessDebugEvents: boolean;
  canAccessDebugThreads: boolean;
  canAccessDebugAgents: boolean;
  canAccessDebugPlugins: boolean;

  // Loading and error states
  loading: boolean;
  error: string | null;

  // Actions
  refreshAccess: () => Promise<void>;
  clearCache: () => void;
  checkDebugAccess: (feature: string) => Promise<boolean>;

  // Role checking utilities
  hasRole: (role: UserRole) => boolean;
  hasMinimumRole: (minimumRole: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;

  // Feature access utilities
  isAgentAccessible: (agentType: string) => boolean;
  isPluginAccessible: (pluginType: string) => boolean;
}

export const useRoleAccess = (options: UseRoleAccessOptions = {}): UseRoleAccessReturn => {
  const {
    uid,
    environment = "local",
    autoRefresh = true,
    refreshInterval = 360 * 60 * 1000, // 360 minutes
  } = options;

  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [accessSummary, setAccessSummary] = useState<UserAccessSummary | null>(null);
  const [loading, setLoading] = useState(!!uid); // Start loading if we have a uid
  const [error, setError] = useState<string | null>(null);

  // Memoized access flags - only calculate when we have valid data
  const accessibleAgents = useMemo(() => {
    if (!accessSummary || loading) return [];
    return accessSummary.accessibleAgents || [];
  }, [accessSummary, loading]);

  const accessiblePlugins = useMemo(() => {
    if (!accessSummary || loading) return [];
    return accessSummary.accessiblePlugins || [];
  }, [accessSummary, loading]);

  const canAccessDebugMenu = useMemo(() => {
    if (!accessSummary || loading) return false;
    return accessSummary.canAccessDebugMenu || false;
  }, [accessSummary, loading]);

  const canAccessDebugEvents = useMemo(() => {
    if (!accessSummary || loading || !userRole) return false;
    return canAccessDebugMenu && (userRole === "admin" || userRole === "tester");
  }, [canAccessDebugMenu, userRole, accessSummary, loading]);

  const canAccessDebugThreads = useMemo(() => {
    if (!accessSummary || loading || !userRole) return false;
    return canAccessDebugMenu && userRole === "admin";
  }, [canAccessDebugMenu, userRole, accessSummary, loading]);

  const canAccessDebugAgents = useMemo(() => {
    if (!accessSummary || loading || !userRole) return false;
    return canAccessDebugMenu && (userRole === "admin" || userRole === "tester");
  }, [canAccessDebugMenu, userRole, accessSummary, loading]);

  const canAccessDebugPlugins = useMemo(() => {
    if (!accessSummary || loading || !userRole) return false;
    return canAccessDebugMenu && userRole === "admin";
  }, [canAccessDebugMenu, userRole, accessSummary, loading]);

  // Fetch user access summary
  const fetchAccessSummary = useCallback(async () => {
    if (!uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const summary = await roleService.getUserAccessSummary(uid);
      setAccessSummary(summary);
      setUserRole(summary.role);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch access summary");
      console.error("Error fetching access summary:", err);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  // Refresh access (public method)
  const refreshAccess = useCallback(async () => {
    await fetchAccessSummary();
  }, [fetchAccessSummary]);

  // Clear cache
  const clearCache = useCallback(() => {
    if (uid) {
      roleService.clearUserCache(uid);
    }
  }, [uid]);

  // Check debug feature access
  const checkDebugAccess = useCallback(
    async (feature: string): Promise<boolean> => {
      if (!uid) return false;
      return await roleService.canAccessDebugFeature(uid, feature, environment);
    },
    [uid, environment]
  );

  // Role checking utilities
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      if (!userRole) return false;

      const roleHierarchy: Record<UserRole, number> = {
        admin: 4,
        tester: 3,
        beta: 2,
        user: 1,
      };

      return roleHierarchy[userRole] >= roleHierarchy[role];
    },
    [userRole]
  );

  const hasMinimumRole = useCallback(
    (minimumRole: UserRole): boolean => {
      return hasRole(minimumRole);
    },
    [hasRole]
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!userRole) return false;
      return roles.some((role) => hasRole(role));
    },
    [userRole, hasRole]
  );

  // Feature access utilities
  const isAgentAccessible = useCallback(
    (agentType: string): boolean => {
      return accessibleAgents.includes(agentType);
    },
    [accessibleAgents]
  );

  const isPluginAccessible = useCallback(
    (pluginType: string): boolean => {
      return accessiblePlugins.includes(pluginType);
    },
    [accessiblePlugins]
  );

  // Auto-refresh effect
  useEffect(() => {
    if (!uid || !autoRefresh) return;

    const interval = setInterval(() => {
      fetchAccessSummary();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [uid, autoRefresh, refreshInterval, fetchAccessSummary]);

  // Initial fetch
  useEffect(() => {
    if (uid) {
      fetchAccessSummary();
    }
  }, [uid, fetchAccessSummary]);

  return {
    // User role and access
    userRole,
    accessSummary,
    accessibleAgents,
    accessiblePlugins,

    // Feature access flags
    canAccessDebugMenu,
    canAccessDebugEvents,
    canAccessDebugThreads,
    canAccessDebugAgents,
    canAccessDebugPlugins,

    // Loading and error states
    loading,
    error,

    // Actions
    refreshAccess,
    clearCache,
    checkDebugAccess,

    // Role checking utilities
    hasRole,
    hasMinimumRole,
    hasAnyRole,

    // Feature access utilities
    isAgentAccessible,
    isPluginAccessible,
  };
};
